import React from "react";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import { useHistory, matchPath, useLocation } from "react-router";
import { configSelector } from "ccui-framework";
import { isSignedInSelector } from "ccui-framework";
import {
  CardsIcon,
  ContactUsIcon,
  DashboardIcon,
  FacebookIcon,
  FAQIcon,
  LiabilitiesIcon,
  LinkedinIcon,
  OrdersIcon,
  PaymentMethodsIcon,
  PaymentsIcon,
  RateSheetIcon,
  TwitterIcon,
  UploadSpreadsheetIcon,
  YoutubeIcon,
} from "./Icons";
import { PrimaryButton } from "../Button";

const mediaHeight715 = `@media (max-height: 715px)`;

const Object3 = styled.span`
  position: relative;
  width: 69px;
  height: 69px;
  background: #ffffff;
  opacity: 0.08;
  border-radius: 40px 0 0 0;

  ${mediaHeight715} {
    height: 50px;
    bottom: 0rem;
  }
`;

const Object2 = styled.span`
  position: relative;
  width: 69px;
  height: 69px;
  background: #ffffff;
  opacity: 0.04;
  top: 69px;

  ${mediaHeight715} {
    height: 32px;
    top: 4rem;
  }
`;

const Object1 = styled.span`
  position: relative;
  width: 142px;
  height: 142px;
  background: #ffffff;
  opacity: 0.08;
  border-radius: 0 90px 0 0;
  bottom: 69px;

  ${mediaHeight715} {
    bottom: 2rem;
    height: 75px;
  }
`;

const Background = styled.div`
  display: flex;
  position: absolute;
  bottom: 1px;

  ${mediaHeight715} {
    bottom: 10px;
  }
`;

const ItemText = styled.span`
  margin-top: -0.1rem;
`;

const Item = styled.a`
  display: flex;
  cursor: pointer;
  font-family: Montserrat, serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  color: #ffffff;
  padding: 1rem;
  margin-left: 15px;
  height: 40px;

  &:last-of-type {
    margin-bottom: 1rem;
  }

  &:hover {
    background: #37332d;
    border-radius: 80px;
    color: #ffffff;
    width: 90%;
    text-decoration: none;
  }
`;

const SubMenu = styled.div`
  flex-direction: column;
  display: flex;
  justify-content: space-evenly;
`;

const GeneralMenu = styled(SubMenu)`
  flex: 0.2;
  ${mediaHeight715} {
    flex: 0.1;
  }
`;

const AccountMenu = styled(SubMenu)`
  flex: 0.3;
  ${mediaHeight715} {
    flex: 0.2;
  }
`;

const SupportMenu = styled(SubMenu)`
  flex: 0.2;
  ${mediaHeight715} {
    flex: 0.1;
  }
`;

const SocialMedia = styled(SubMenu)`
  z-index: 2;
  flex-direction: row;
  position: absolute;
  bottom: 15px;
  ${mediaHeight715} {
    bottom: 10px;
  }
`;

const MenuHeader = styled.h1`
  font-family: Montserrat, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 12px;
  line-height: 12px;
  color: #c4c4c4;
  text-transform: uppercase;
  align-items: flex-start;
  margin: 0.2rem 0 0.2rem 30px;
`;

const SellButton = styled(PrimaryButton)`
  margin: 1.5rem;
  width: 215px;
`;

const Menu = styled.div`
  flex-direction: column;
  display: flex;
  flex: 1;
`;

const Container = styled.div`
  background: #1f2021;
  border-radius: 0 0 48px 0;
`;

const SocialLink = styled.a`
  &:first-of-type {
    margin-left: 1.5rem;
  }
  margin-left: 0.5rem;
  margin-right: 0.5rem;
`;

const Sidebar = ({ isOpen = false, onClose = () => {} }) => {
  const history = useHistory();
  const location = useLocation();
  const config = useSelector((state) => configSelector(state));
  const isSignedIn = useSelector((state) => isSignedInSelector(state));
  const isActiveMenuItem = {
    background: "#37332d",
    borderRadius: "80px",
    color: "#ffffff",
    width: "90%",
    textDecoration: "none",
  };

  return isSignedIn ? (
    <>
      {/* Overlay — mobile only */}
      <div
        className={`md:hidden fixed inset-0 z-[199] ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      <Container
        id="sideBar"
        className={`md:relative md:translate-x-0 md:w-[250px] md:max-h-[calc(100vh-65px)] absolute top-0 left-0 w-[96%] min-h-[calc(100vh-65px)] z-[200] ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SellButton
          onClick={() => {
            history.push("/new-order/enter-cards");
            onClose();
          }}
        >
          Sell Cards
        </SellButton>
        <Menu>
          <MenuHeader>General</MenuHeader>
          <GeneralMenu>
            {/* <Item
              key={"Dashboard"}
              style={
                matchPath(location.pathname, { path: "/dashboard" })
                  ? isActiveMenuItem
                  : {}
              }
              onClick={() => {
                history.push("/");
              }}
            >
              <DashboardIcon
                isActive={matchPath(location.pathname, { path: "/dashboard" })}
              />
              <ItemText>Dashboard</ItemText>
            </Item> */}
            <Item
              key={"RateSheet"}
              style={
                matchPath(location.pathname, { path: "/current-rates" })
                  ? isActiveMenuItem
                  : {}
              }
              onClick={() => {
                history.push("/current-rates");
                onClose();
              }}
            >
              <RateSheetIcon
                isActive={matchPath(location.pathname, {
                  path: "/current-rates",
                })}
              />
              <ItemText>Rate Sheet</ItemText>
            </Item>
          </GeneralMenu>
          <MenuHeader>Account</MenuHeader>
          <AccountMenu>
            <Item
              key={"Orders"}
              style={
                matchPath(location.pathname, { path: "/account/orders" })
                  ? isActiveMenuItem
                  : {}
              }
              onClick={() => {
                history.push("/account/orders");
                onClose();
              }}
            >
              <OrdersIcon
                isActive={matchPath(location.pathname, {
                  path: "/account/orders",
                })}
              />
              <ItemText>Orders</ItemText>
            </Item>
            <Item
              key={"Cards"}
              style={
                matchPath(location.pathname, { path: "/account/cards" })
                  ? isActiveMenuItem
                  : {}
              }
              onClick={() => {
                history.push("/account/cards");
                onClose();
              }}
            >
              <CardsIcon
                isActive={matchPath(location.pathname, {
                  path: "/account/cards",
                })}
              />
              <ItemText>Cards</ItemText>
            </Item>
            <Item
              key={"Liabilities"}
              style={
                matchPath(location.pathname, { path: "/account/liabilities" })
                  ? isActiveMenuItem
                  : {}
              }
              onClick={() => {
                history.push("/account/liabilities");
                onClose();
              }}
            >
              <LiabilitiesIcon
                isActive={matchPath(location.pathname, {
                  path: "/account/liabilities",
                })}
              />
              <ItemText>Liabilities</ItemText>
            </Item>
            <Item
              key={"Payments"}
              style={
                matchPath(location.pathname, { path: "/account/payments" })
                  ? isActiveMenuItem
                  : {}
              }
              onClick={() => {
                history.push("/account/payments");
                onClose();
              }}
            >
              <PaymentsIcon
                isActive={matchPath(location.pathname, {
                  path: "/account/payments",
                })}
              />
              <ItemText>Payments</ItemText>
            </Item>
            <Item
              key={"PaymentMethods"}
              style={
                matchPath(location.pathname, {
                  path: "/account/payment-methods",
                })
                  ? isActiveMenuItem
                  : {}
              }
              onClick={() => {
                history.push("/account/payment-methods");
                onClose();
              }}
            >
              <PaymentMethodsIcon
                isActive={matchPath(location.pathname, {
                  path: "/account/payment-methods",
                })}
              />
              <ItemText>Payment Methods</ItemText>
            </Item>
          </AccountMenu>
          <MenuHeader>Support</MenuHeader>
          <SupportMenu>
            <Item
              key={"Contact"}
              style={
                matchPath(location.pathname, {
                  path: "/contact",
                })
                  ? isActiveMenuItem
                  : {}
              }
              onClick={() => {
                history.push("/contact");
                onClose();
              }}
            >
              <ContactUsIcon
                isActive={matchPath(location.pathname, {
                  path: "/contact",
                })}
              />
              <ItemText>Contact Us</ItemText>
            </Item>
            <Item
              key={"FAQ"}
              style={
                matchPath(location.pathname, { path: "/faq" })
                  ? isActiveMenuItem
                  : {}
              }
              onClick={() => {
                history.push("/faq");
                onClose();
              }}
            >
              <FAQIcon
                isActive={matchPath(location.pathname, { path: "/faq" })}
              />
              <ItemText>FAQ</ItemText>
            </Item>
            <Item
              key={"Spreadsheet"}
              style={
                matchPath(location.pathname, {
                  path: "/spreadsheet-instructions",
                })
                  ? isActiveMenuItem
                  : {}
              }
              onClick={() => {
                history.push("/spreadsheet-instructions");
                onClose();
              }}
            >
              <UploadSpreadsheetIcon
                isActive={matchPath(location.pathname, {
                  path: "/spreadsheet-instructions",
                })}
              />
              <ItemText>Upload Spreadsheet</ItemText>
            </Item>
          </SupportMenu>
        </Menu>
        <SocialMedia>
          <SocialLink
            href={`${config.socialMediaData["facebook"]}`}
            target="_blank"
          >
            <FacebookIcon />
          </SocialLink>
          <SocialLink
            href={`${config.socialMediaData["twitter"]}`}
            target="_blank"
          >
            <TwitterIcon />
          </SocialLink>
          <SocialLink
            href={`${config.socialMediaData["youtube"]}`}
            target="_blank"
          >
            <YoutubeIcon />
          </SocialLink>
          <SocialLink
            href={`${config.socialMediaData["linkedin"]}`}
            target="_blank"
          >
            <LinkedinIcon />
          </SocialLink>
        </SocialMedia>
        <Background>
          <Object1 />
          <Object2 />
          <Object3 />
        </Background>
      </Container>
    </>
  ) : null;
};

Sidebar.propTypes = {};

export { Sidebar };
